/**
 * DataLayer utility for GTM + GA4 event tracking (SPA).
 * All events use window.dataLayer.push() for GTM to consume.
 * Naming and payloads follow GA4 + conversion best practices.
 * Duplicate prevention is handled by callers via refs/flags.
 */

const SESSION_START_KEY = 'ga_session_start_sent';

function ensureDataLayer() {
  if (typeof window === 'undefined') return false;
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  return true;
}

/**
 * Push a single event to dataLayer (no duplicate logic here; caller controls).
 * @param {string} event - GA4 event name
 * @param {Record<string, unknown>} [params] - Event parameters
 */
export function pushEvent(event, params = {}) {
  if (!ensureDataLayer()) return;
  window.dataLayer.push({
    event,
    ...params,
  });
}

/**
 * page_view – fire on every virtual page change (SPA route), including initial load.
 * Do NOT assume GTM/GA4 auto-detects SPA page views.
 */
export function pushPageView({ page_path, page_title, page_location } = {}) {
  const path = page_path ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const title = page_title ?? (typeof document !== 'undefined' ? document.title : '');
  const location = page_location ?? (typeof window !== 'undefined' ? window.location.href : '');
  pushEvent('page_view', {
    page_path: path,
    page_title: title,
    page_location: location,
  });
}

/**
 * session_start – fire once per browser session (first page).
 * Uses sessionStorage to avoid duplicate.
 */
export function pushSessionStart() {
  if (typeof sessionStorage === 'undefined') return;
  if (sessionStorage.getItem(SESSION_START_KEY)) return;
  sessionStorage.setItem(SESSION_START_KEY, '1');
  pushEvent('session_start');
}

/**
 * form_view – fired when the form enters the viewport (caller uses IntersectionObserver + ref to fire once).
 */
export function pushFormView(formName, extra = {}) {
  pushEvent('form_view', { form_name: formName, ...extra });
}

/**
 * form_start – first interaction with any form field (focus/input/select); fire once per form (caller uses ref).
 */
export function pushFormStart(formName, extra = {}) {
  pushEvent('form_start', { form_name: formName, ...extra });
}

/**
 * submit_attempt – fired when submit button is clicked (before validation / before API call).
 */
export function pushSubmitAttempt(formName, extra = {}) {
  pushEvent('submit_attempt', { form_name: formName, ...extra });
}

/**
 * lead – conversion. Fire ONLY after confirmed server success (HTTP 2xx + valid response).
 * Configure this event as a GA4 Conversion in GA4 admin.
 */
export function pushLead(formName, options = {}) {
  pushEvent('lead', {
    form_name: formName,
    value: options.value ?? 0,
    currency: options.currency ?? 'EGP',
    ...options,
  });
}

/**
 * Scroll depth – fire once per threshold (caller uses refs to prevent duplicates).
 */
export function pushScrollDepth(percent) {
  const allowed = [25, 50, 75];
  if (!allowed.includes(percent)) return;
  pushEvent(`scroll_${percent}`, { scroll_depth_percent: percent });
}

/**
 * Time on page – fire once per threshold (caller uses refs).
 */
export function pushTimeOnPage(seconds) {
  const allowed = [10, 30];
  if (!allowed.includes(seconds)) return;
  pushEvent(`time_${seconds}s`, { time_seconds: seconds });
}

/**
 * CTA hover – optional engagement event for key CTAs.
 */
export function pushCtaHover(ctaName, extra = {}) {
  pushEvent('cta_hover', { cta_name: ctaName, ...extra });
}
